package com.downdogapp.client

import com.downdogapp.*
import com.downdogapp.client.api.SettingSelectorSection
import com.downdogapp.client.views.start.StartViewConstants

object SettingsDrawerUtil {

  val firstAnimationKeyframe = 0.25
  val secondAnimationKeyframe = 0.5
  val thirdAnimationKeyframe = 0.75

  val contentTopMarginCollapsed = 30
  val contentTopMarginExpanded = 15
  val touchPadding = 50
  private val expandedTopPadding = 60

  fun headerTileHeight(largeTiles: Boolean) = if (largeTiles) 50 else 40
  val settingTileHorizontalMargin = 15
  val settingTileVerticalMarginCollapsed = if (Util.isSmallPhone) 8 else 12
  fun settingTileVerticalMarginExpanded(largeTiles: Boolean) = when {
    largeTiles -> 15
    Util.isSmallPhone -> 8
    else -> 10
  }

  val settingTileHeightCollapsed = if (Util.isSmallPhone) 55 else 65
  fun settingTileHeightExpanded(largeTiles: Boolean) = when {
    largeTiles -> 75
    Util.isSmallPhone -> 50
    else -> 60
  }

  val settingTileGridMargin = 8

  val startButtonHeight = 54 // ROUNDED_TEXT_BUTTON_HEIGHT
  val startButtonBottomMargin = StartViewConstants.TAB_BAR_HEIGHT + 12 + Util.safeAreaBottomMargin

  fun getStateFromFrameHeight(height: Double): DrawerHeightConfig {
    val preferredOptions = listOf(
      DrawerHeightConfig(allowSections = true, largeTiles = true),
      DrawerHeightConfig(allowSections = true, largeTiles = false),
      DrawerHeightConfig(allowSections = false, largeTiles = false)
    )
    return preferredOptions.firstOrNull { getDrawerHeightExpanded(it) < height - expandedTopPadding }
           ?: preferredOptions.last()
  }

  fun getDrawerHeightCollapsed() =
    startButtonBottomMargin + contentTopMarginCollapsed + startButtonHeight + 4 +
    numRowsCollapsed() * (settingTileGridMargin + settingTileHeightCollapsed)

  fun getDrawerHeightExpanded(drawerHeightConfig: DrawerHeightConfig) =
    contentTopMarginExpanded.toDouble() + Util.safeAreaBottomMargin +
    if (drawerHeightConfig.allowSections)
      10 + SequenceSettings.selectorSections.sumOf { getSectionHeightExpanded(it, drawerHeightConfig.largeTiles) }
    else
      getSettingExpandedTopMargin(
        numRows(SequenceSettings.selectorSections.sumOf { it.items.size }),
        drawerHeightConfig.largeTiles
      )

  fun getSectionHeightExpanded(section: SettingSelectorSection, largeTiles: Boolean) =
    headerTileHeight(largeTiles) + numRowsExpanded(section) * (settingTileGridMargin + settingTileHeightExpanded(
      largeTiles
    ))

  fun getTilePositionExpanded(section: SettingSelectorSection, indexInSection: Int, largeTiles: Boolean) =
    TilePosition(
      isLeftColumn = indexInSection % 2 == 0,
      topMargin = getSettingExpandedTopMargin(section, indexInSection / 2, largeTiles)
    )

  fun getTilePositionExpanded(indexInSection: Int, largeTiles: Boolean) =
    TilePosition(
      isLeftColumn = indexInSection % 2 == 0,
      topMargin = getSettingExpandedTopMargin(indexInSection / 2, largeTiles)
    )

  fun getPositionsCollapsed(): Map<Int, TilePosition> {
    val numLeftColumn = (MANIFEST.numRecentSettingsToShow / 2.0).ceil()
    val allSelectorTypeIds = SequenceSettings.allSelectorTypeIds
    val recentSettings = SequenceSettings.getRecentSelectorTypeIds(MANIFEST.numRecentSettingsToShow)
      .sortedBy { allSelectorTypeIds.indexOf(it) }
    return recentSettings.withIndex().associate {
      it.value to TilePosition(
        isLeftColumn = it.index < numLeftColumn,
        topMargin = getSettingCollapsedTopMargin(it.index % numLeftColumn)
      )
    }
  }

  fun getHeaderTopMargin(targetSection: SettingSelectorSection, largeTiles: Boolean): Int {
    var sum = contentTopMarginExpanded
    for (it in SequenceSettings.selectorSections) {
      if (it == targetSection) {
        return sum
      }
      sum += getSectionHeightExpanded(it, largeTiles)
    }
    error("SettingsDrawer: Getting top margin for section ${targetSection.title} not sequence settings")
  }

  private fun getSettingCollapsedTopMargin(row: Int): Int =
    contentTopMarginCollapsed + (settingTileHeightCollapsed + settingTileGridMargin) * row

  private fun getSettingExpandedTopMargin(targetSection: SettingSelectorSection, row: Int, largeTiles: Boolean): Int {
    var sum = contentTopMarginExpanded
    for (it in SequenceSettings.selectorSections) {
      if (it == targetSection) {
        return sum + getSettingExpandedTopMargin(row, largeTiles)
      }
      sum += getSectionHeightExpanded(it, largeTiles)
    }
    error("SettingsDrawer: Getting top margin for section ${targetSection.title} not sequence settings")
  }

  private fun getSettingExpandedTopMargin(row: Int, largeTiles: Boolean): Int =
    headerTileHeight(largeTiles) + (settingTileHeightExpanded(largeTiles) + settingTileGridMargin) * row

  private fun numRowsCollapsed() =
    numRows(numTiles = MANIFEST.numRecentSettingsToShow)

  fun numRowsExpanded(section: SettingSelectorSection) =
    numRows(numTiles = section.items.size)

  private fun numRows(numTiles: Int) =
    (numTiles / 2.0).ceil()
}

data class TilePosition(val isLeftColumn: Boolean, val topMargin: Int)

data class DrawerHeightConfig(val allowSections: Boolean, val largeTiles: Boolean)
